import { z } from 'zod';
import {
  TaskType,
  taskTypeZodWithCatch,
} from 'packages/lib/api/annotations/contract/enums';

/** Allowed GroupType query param values for backend APIs */
export const groupTypeZod = z.enum([
  'requestID',
  'pipeline',
  'workflow',
  'sampleGroup',
  'taskGroup',
]);
export type GroupType = z.infer<typeof groupTypeZod>;

/**
 * One row of `GET /cost/group/{token}`, covering every task under that group that
 * survived the startDate/endDate, taskType and managerEmail filters.
 */
export const costGroupItemZod = z.object({
  token: z.string(),
  name: z.string(),
  taskType: taskTypeZodWithCatch,
  avgLabelingCostPerTask: z.number(),
  /** Null when all tasks being averaged have no frames */
  avgLabelingCostPerFrame: z.number().nullable(),
  avgLabelingCostPerAnnotation: z.number(),
  summediMeritLabelingCost: z.number(),
  summediMeritQACost: z.number(),
  summediMeritAuditCost: z.number(),
  summedScaleCost: z.number(),
  /** Total number of tasks included in these averages and sums. */
  summedNumTasks: z.number(),
  /** Annotation count per task, summed for all included tasks. */
  summedNumAnnotations: z.number(),
  /** Frame count per task, summed for all included tasks. Null when N/A. */
  summedNumFrames: z.number().nullable(),
});
export type CostGroupItem = z.infer<typeof costGroupItemZod>;

export const costGroupResponseZod = z.object({
  data: z.array(costGroupItemZod),
});
export type CostGroupResponse = z.infer<typeof costGroupResponseZod>;

/**
 * `metric` query param of `GET /cost/group/{token}/time-series`, which decides the unit
 * of every value and, for `comparison`, the keys as well. Averages (`avgCost`,
 * `avgTasks`, `avgHours`) and throughput metrics are planned for later phases.
 */
export const costMetricZod = z.enum([
  'totalCost',
  'totalTasks',
  'totalHours',
  'comparison',
]);
export type CostMetric = z.infer<typeof costMetricZod>;

/** YYYY-MM-DD, the x-axis of every time-series point. */
const seriesDateZod = z.object({ date: z.string() });

/**
 * Keys are named for the role rather than the unit, each of these may represent dollars,
 * task counts, or hours depending on the requested CostMetric.
 */
const roleSeriesPointZod = seriesDateZod.extend({
  labeling: z.number(),
  qa: z.number(),
  audit: z.number(),
});
export type RoleSeriesPoint = z.infer<typeof roleSeriesPointZod>;

const costComparisonPointZod = seriesDateZod.extend({
  labeling: z.number(),
  industry: z.number(),
});
export type CostComparisonPoint = z.infer<typeof costComparisonPointZod>;

/**
 * One day of `GET /cost/group/{token}/time-series`. Points arrive in the shape Recharts
 * plots directly: one object per x-axis tick, one numeric key per line.
 */
export type CostSeriesPoint = RoleSeriesPoint | CostComparisonPoint;

/** Every key a line can be plotted from, i.e. every `dataKey` the chart may ask for. */
export type CostSeriesValueKey = Exclude<
  keyof (RoleSeriesPoint & CostComparisonPoint),
  'date'
>;

export const costListResponseZod = <T extends z.ZodTypeAny>(itemZod: T) =>
  z.object({ data: z.array(itemZod) });
export type CostListResponse<T> = { data: T[] };

/**
 * Given a CostMetric, returns the appropriate Zod parser for the API's response
 */
export const costSeriesResponseZodByMetric: Record<
  CostMetric,
  z.ZodType<CostListResponse<CostSeriesPoint>>
> = {
  totalCost: costListResponseZod(roleSeriesPointZod),
  totalTasks: costListResponseZod(roleSeriesPointZod),
  totalHours: costListResponseZod(roleSeriesPointZod),
  comparison: costListResponseZod(costComparisonPointZod),
};
export type CostSeriesResponse = CostListResponse<CostSeriesPoint>;

/** Shared by both cost endpoints. */
export interface CostQuery {
  /** Token or request ID, matching `groupType`. */
  token: string;
  groupType: GroupType;
  /** YYYY-MM-DD */
  startDate: string;
  /** YYYY-MM-DD */
  endDate: string;
  /** Empty means all task types, in which case the param is omitted. */
  taskTypes: TaskType[];
  /** The signed-in user's email when the Contributors menu is on Direct Reports. */
  managerEmail?: string;
}

/** The time-series endpoint takes the same filters plus the chart's selected metric. */
export interface CostSeriesQuery extends CostQuery {
  metric: CostMetric;
}
